import { createApi } from '@reduxjs/toolkit/query/react'
import type { EntityDownloadUrls } from '@tunasong/models'
import { logger } from '@tunasong/models'
import type { StorageUrls } from '@tunasong/schemas'
import { storageSlice } from '../features/storage/storage-slice.js'
import baseQuery, { createCachedQuerySpecHelper } from './base-query.js'

const apiName = 'storage-api'
const cachedQuerySpec = createCachedQuerySpecHelper(apiName)

export const storageApi = createApi({
  /** We currently have 24 hour validity for the S3 tokens */
  keepUnusedDataFor: 0,
  reducerPath: apiName,

  baseQuery,
  endpoints: builder => ({
    getEntityDownloadUrls: builder.query<EntityDownloadUrls, { entityId: string }>({
      query: ({ entityId }) => `entities/${entityId}/download`,
      onQueryStarted: async ({ entityId }, { dispatch, queryFulfilled }) => {
        dispatch(storageSlice.actions.setLoading({ entityId, loading: true }))
        await queryFulfilled
          .then(result => {
            if (result.data.wildcard) {
              const wildcardAuthQuery = new URL(result.data.wildcard).search
              dispatch(storageSlice.actions.setWildcardAuthQuery({ wildcardAuthQuery }))
            }
          })
          /**
           * `.finally()` does NOT handle a rejection — it re-throws — and RTK
           * attaches no catch to `onQueryStarted`, so a denied download (403)
           * escaped as an unhandled rejection (GH #1731). Catch BEFORE the
           * finally so the loading flag still clears either way.
           */
          .catch(e => {
            logger.warn('Error loading entity download urls', e)
          })
          .finally(() => {
            dispatch(storageSlice.actions.setLoading({ entityId, loading: false }))
          })
      },
    }),

    getUploadUrl: builder.query<
      { url: string },
      { filename: string; contentType: string; metadata?: Record<string, string> }
    >({
      query: ({ filename, contentType = 'audio/wav', metadata }) => ({
        url: 'util/upload',
        method: 'POST',
        body: { filename, contentType, metadata },
      }),
    }),
    /** Download the stored file. Requires a wildcard auth query */
    getFileJSON: builder.query<unknown, { storagePath?: string }>({
      query: ({ storagePath }) => ({
        url: `https://tunasong.com/${storagePath}`,
      }),
    }),
    /** Use this with caution as we don't want to crash the browser */
    getAuthorizedStorageFile: builder.query<Uint8Array, { storageUrls: StorageUrls }>({
      // @note we don't use an in-memory cache here. We rely on the Service Worker. We don't mutate the data.
      query: cachedQuerySpec('getAuthorizedStorageFile', ({ storageUrls }) => ({
        url: storageUrls.url,
        headers: {
          // Ignore the disk cache to avoid browser caching old docs
          'cache-control': 'max-age=0',
        },
        responseHandler: async response => {
          const buffer = await response.arrayBuffer()
          // We allow up to 10MB files to be downloaded into memory
          if (buffer.byteLength > 10 * 1024 * 1024) {
            throw new Error('File too large to download')
          }
          return new Uint8Array(buffer)
        },
      })),
    }),

    /** signedUrl is the result of getUploadUrl. We cannot use Authorization header for this */
    uploadFile: builder.mutation<void, { signedUrl: string; data: ArrayBuffer; contentType: string }>({
      extraOptions: {
        skipAuthorization: true,
      },
      query: ({ signedUrl, data, contentType }) => ({
        url: signedUrl,
        method: 'PUT',
        headers: {
          'Content-Type': contentType,
          /** Must match the CacheControl in the presigned URL PutObjectCommand */
          'Cache-Control': 'public, max-age=31536000, immutable',
        },
        body: data,
      }),
    }),
  }),
})
